import React, {useMemo} from "react";
import {usePage} from "@inertiajs/react";
import {Container} from "react-bootstrap";
import {createColumnHelper} from "@tanstack/react-table";
import DataGrid from "../../Components/DataGrid/DataGrid.jsx";
import HelpTooltip from "../../Components/HelpTooltip.jsx";
import ArticleShopLinks from "../../Components/ArticleShopLinks.jsx";
import {
    formatChangeValue,
    formatLogTimestamp,
    formatSourceText,
    formatSystemLabel,
    systemBadgeVariant,
} from "../../functions/stockChangeLogFormat.js";

const columnHelper = createColumnHelper();

export default function ChangeLog() {
    const {systems, change_types: changeTypes, sources, users} = usePage().props;

    // Auswahllisten kommen vom Server (Quelle der Wahrheit für die Labels),
    // hier nur um die "Alle"-Option ergänzt.
    const systemSelectOptions = useMemo(() => [{value: "", label: "Alle"}, ...(systems || [])], [systems]);
    const changeTypeSelectOptions = useMemo(
        () => [{value: "", label: "Alle"}, ...(changeTypes || [])],
        [changeTypes]
    );
    const sourceSelectOptions = useMemo(
        () => [{value: "", label: "Alle"}, ...(sources || [])],
        [sources]
    );

    const columns = useMemo(
        () => [
            columnHelper.accessor("changed_at", {
                id: "changed_at",
                header: "Zeitpunkt",
                cell: (info) => formatLogTimestamp(info.getValue()),
                meta: {
                    width: 150,
                    help: "Zeitpunkt der Änderung. Bei übernommener Alt-Historie der ursprüngliche Zeitpunkt, nicht der Zeitpunkt der Übernahme.",
                },
            }),
            columnHelper.accessor("artikelnummer", {
                id: "artikelnummer",
                header: "Artikelnummer",
                cell: (info) => (
                    <ArticleShopLinks
                        sku={info.getValue()}
                        b2cDeId={info.row.original.b2c_de_id}
                        b2bStatus={info.row.original.b2b_status}
                        weclappId={info.row.original.weclapp_id}
                    />
                ),
                meta: {
                    filterVariant: "text",
                    help: "Die Artikelnummer (SKU). Darunter Schaltflächen zum jeweiligen System, sofern der Artikel dort bekannt ist.",
                },
            }),
            columnHelper.accessor("bezeichnung", {
                id: "bezeichnung",
                header: "Bezeichnung",
                // Der Name stammt aus dem Artikelstamm. Fehlt dort der Satz,
                // bleibt das Feld leer - dann ein Strich statt einer leeren
                // Zelle, damit man sieht, dass nichts fehlt, sondern nichts da ist.
                cell: (info) => info.getValue() || <span className="text-muted">–</span>,
                meta: {
                    width: 260,
                    help: "Bezeichnung des Artikels aus dem Artikelstamm. Die Suche oben findet Artikel auch über ihren Namen.",
                },
            }),
            columnHelper.accessor("system", {
                id: "system",
                header: "System",
                cell: (info) => (
                    <span className={`badge bg-${systemBadgeVariant(info.getValue())}`}>
                        {formatSystemLabel(info.getValue())}
                    </span>
                ),
                meta: {
                    filterVariant: "select",
                    selectOptions: systemSelectOptions,
                    width: 110,
                    help: "In welchem System die Änderung stattfand: Weclapp (Lagerbestand), B2C-Shop (shop.molotow.com) oder B2B-Shop (b2b.molotow.com).",
                },
            }),
            // Accessor bewusst auf "change_type" (nicht auf einen eigenen
            // "change"-Schlüssel), damit der Spaltenfilter exakt auf
            // filter[change_type] zielt - die Zelle selbst zeigt trotzdem die
            // VOLLSTÄNDIGE Änderung (alt -> neu), nicht nur die Änderungsart.
            columnHelper.accessor("change_type", {
                id: "change_type",
                header: "Änderung",
                enableSorting: false,
                cell: (info) => {
                    const row = info.row.original;
                    const changeType = info.getValue();
                    const oldLabel = formatChangeValue(row.old_value, changeType);
                    const newLabel = formatChangeValue(row.new_value, changeType);
                    return (
                        <span>
                            {oldLabel} → {newLabel}
                            {changeType === "quantity" && row.warehouse_name && (
                                <span className="text-muted"> ({row.warehouse_name})</span>
                            )}
                        </span>
                    );
                },
                meta: {
                    filterVariant: "select",
                    selectOptions: changeTypeSelectOptions,
                    help: "Alter Wert → neuer Wert. Bei Mengenänderungen zusätzlich das betroffene Lager, bei Verfügbarkeitsänderungen der Klartext-Status (Auf Lager/In Zulauf). Der Filter dahinter schränkt auf die ÄnderungsART ein (Menge/Verfügbarkeit).",
                },
            }),
            columnHelper.accessor("source", {
                id: "source",
                header: "Auslöser",
                cell: (info) => {
                    const row = info.row.original;
                    return (
                        <div className="d-flex flex-column">
                            <span>{formatSourceText(row)}</span>
                            {row.trigger && <span className="text-muted small">{row.trigger}</span>}
                        </div>
                    );
                },
                meta: {
                    filterVariant: "select",
                    selectOptions: sourceSelectOptions,
                    help: "Wodurch die Änderung ausgelöst wurde: ein automatischer Lauf (z. B. der stündliche B2B-Abgleich), eine manuelle Aktion (mit Benutzername) oder die einmalige Übernahme der Alt-Historie beim Aufbau dieses Logs. Darunter der technische Auslöser (z. B. „b2b:stock-sync\").",
                },
            }),
            columnHelper.accessor("reason", {
                id: "reason",
                header: "Grund",
                enableSorting: false,
                cell: (info) => info.getValue() || "–",
                meta: {
                    help: "Freitext-Begründung der Änderung, sofern beim Schreiben mitgegeben (z. B. „SNV-Bestand < Schwelle\"). Über die Suche oben ebenfalls durchsuchbar.",
                },
            }),
        ],
        [systemSelectOptions, changeTypeSelectOptions, sourceSelectOptions]
    );

    const additionalFilters = useMemo(
        () => [
            {
                key: "von",
                type: "date",
                label: "Von",
                defaultValue: "",
                help: "Nur Änderungen ab diesem Datum (inklusive), bezogen auf den Zeitpunkt der Änderung.",
            },
            {
                key: "bis",
                type: "date",
                label: "Bis",
                defaultValue: "",
                help: "Nur Änderungen bis zu diesem Datum (inklusive), bezogen auf den Zeitpunkt der Änderung.",
            },
            {
                key: "user",
                type: "select",
                label: "Benutzer",
                defaultValue: "",
                options: [{value: "", label: "Alle"}, ...(users || [])],
                help: "Nur manuelle Änderungen dieses Benutzers.",
            },
        ],
        [users]
    );

    return (
        <Container fluid className="pt-2">
            <div className="mt-1 mb-3 d-flex align-items-center gap-2">
                <h1 className="mb-0">Lager – Änderungs-Log</h1>
                <HelpTooltip placement="right" size={0.95}>
                    <div className="text-start">
                        <strong>Was zeigt dieses Log?</strong>
                        <div className="mt-1">
                            Jede einzelne Mengen- oder Verfügbarkeitsänderung je Artikel, dauerhaft und
                            lückenlos protokolliert – von Weclapp-Lagerbeständen bis zu Statuswechseln
                            im B2C- und B2B-Shop.
                        </div>
                        <div className="mt-1">
                            Anders als die Lauf-Protokolle der Abgleiche (die nur den Verlauf eines
                            gesamten Laufs zeigen) steht hier jeder einzelne Artikel-Eintrag mit Auslöser
                            und Grund.
                        </div>
                    </div>
                </HelpTooltip>
            </div>

            <DataGrid
                columns={columns}
                getRowId={(row) => row.id}
                initialSorting={[{id: "changed_at", desc: true}]}
                emptyLabel="Keine Änderungen gefunden."
                itemLabel="Einträge"
                itemLabelDative="Einträgen"
                dataUrl="/stock/change-log/data"
                additionalFilters={additionalFilters}
                defaultPageSize={100}
            />
        </Container>
    );
}
